import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import FaHome from 'react-icons/lib/fa/home';
import FaTrash from 'react-icons/lib/fa/trash';
import ReactTooltip from 'react-tooltip';
import styled, { css, keyframes } from 'styled-components';

import messages from './messages/PaidFeatures_messages';

import Button from '../../components/Button';
import LoadingIndicator from '../../components/LoadingIndicator';
import LocalitySearchBox from '../common/LocalitySearchBox';

const propTypes = {
  companyRegions: PropTypes.object.isRequired,
  handleUpdate: PropTypes.func.isRequired,
  setReportEditable: PropTypes.func.isRequired,
  municipalities: PropTypes.object,
  regions: PropTypes.object,
  locale: PropTypes.object.isRequired,
  reportEditable: PropTypes.object,
  intl: intlShape.isRequired,
};

class PortfolioLocalitiesEditable extends Component {
  constructor(props) {
    super(props);

    this.state = {
      highlightThis: {},
    };
  }

  get nameKey() {
    const { locale } = this.props;
    return locale.lang === 'en' ? 'name_fi' : `name_${locale.lang}`;
  }

  updateLocalityWholeCountry = evnt => {
    const { target: { checked }} = evnt;
    const { reportEditable: { attributes } } = this.props;

    attributes.locality_whole_country_fi = checked ? '1' : '0';
    this.props.setReportEditable({ attributes });
  }

  selectNewRegion = locality => {
    const { regionId } = locality;
    const {
      companyRegions,
      handleUpdate,
      municipalities,
      regions,
     } = this.props;

    const newRegion = {
      ...regions[regionId],
      whole_region: true,
    };
    newRegion.municipalities = regions[regionId].municipalities.reduce(
      (acc, municipalityId) => {
        acc[municipalityId] = municipalities[municipalityId];
        return acc;
      },
      {}
    );

    if (companyRegions[regionId] && companyRegions[regionId].is_homeregion) {
      const hometown =
        Object.values(companyRegions[regionId].municipalities).find(
          m => m.is_hometown
        );
      newRegion.is_homeregion = true;
      newRegion.municipalities[hometown.id] = hometown;
    }

    this.setState({ highlightThis: locality },
      () => handleUpdate({
        ...companyRegions,
        [regionId]: newRegion,
      })
    );
  }

  selectNewMunicipality = locality => {
    const { municipalityId, regionId } = locality;
    const {
      companyRegions,
      handleUpdate,
      municipalities,
      regions,
    } = this.props;

    const newRegion = {
      ...regions[regionId],
      municipalities: {},
      whole_region: false,
      ...companyRegions[regionId],
    };
    newRegion.municipalities[municipalityId] = municipalities[municipalityId];

    this.setState({ highlightThis: locality },
      () => handleUpdate({
        ...companyRegions,
        [regionId]: newRegion,
      })
    );
  }

  selectLocality = locality => {
    const { regionId, municipalityId } = locality;
    const { companyRegions } = this.props;

    if (municipalityId) {
      // User selected single municipality.
      if (companyRegions[regionId]
        && companyRegions[regionId].municipalities[municipalityId]) {
        // The municipality has already been selected.
        // Highlight the selection
        // Set the highlightThis first empty and trigger update
        // with correct value in callback to reset the CSS-animation
        // for the element. The timeout prevents react from stacking
        // the updates.
        this.setState({ highlightThis: {}}, () =>
          setTimeout(() =>
            this.setState({ highlightThis: locality }),
            0
          )
        );
      } else {
        this.selectNewMunicipality(locality);
      }
    } else if (companyRegions[regionId]
      && companyRegions[regionId].whole_region) {
      // User selected a whole region, which has already been selected.
      // Highlight the selection.
      this.setState({ highlightThis: {}}, () =>
        setTimeout(() =>
          this.setState({ highlightThis: locality }),
          0
        )
      );
    } else {
      // User selected a whole region.
      this.selectNewRegion(locality);
    }
  }

  removeLocation = location => {
    const { regionId, municipalityId, is_hometown: isHometown } = location;
    const { companyRegions, handleUpdate } = this.props;

    if (municipalityId && !isHometown) {
      // User removed single municipality.
      const newRegion = { ...companyRegions[regionId] };
      newRegion.municipalities = Object.values(newRegion.municipalities).reduce(
        (acc, m) => {
          if (m.id !== municipalityId || m.is_hometown) {
            acc[m.id] = m;
          }
          return acc;
        },
        {}
      );
      handleUpdate({
        ...companyRegions,
        [regionId]: newRegion,
      });
    } else {
      // User removed a whole region.
      handleUpdate(
        Object.values(companyRegions).reduce((acc, r) => {
          if (r.id === regionId && r.is_homeregion) {
            // User removed region that contains the company's hometown
            const hometown = Object.values(r.municipalities).find(
              m => m.is_hometown
            );
            // Modify the region so that it's not selected as a whole
            // and only selected municipality is the hometown.
            acc[r.id] = {
              ...r,
              whole_region: false,
              municipalities: { [hometown.id]: hometown },
            };
          } else if (r.id !== regionId) {
            acc[r.id] = r;
          }
          return acc;
        }, {})
      );
    }
  }

  render() {
    const { highlightThis } = this.state;
    const {
      municipalities,
      reportEditable,
      companyRegions,
      regions,
      locale,
      intl,
    } = this.props;

    if (!reportEditable || !companyRegions) {
      return <LoadingIndicator />;
    }

    // Only finland for now...
    let wholeCountry = false;
    if (reportEditable.attributes.locality_whole_country_fi === '1') {
      wholeCountry = true;
    }

    let hometown = {};
    const sortedCompanyLocalities = Object.values(companyRegions)
      .reduce((localities, r) => {
        if (r.whole_region) {
          localities.push({
            type: 'REGION',
            regionId: r.id,
            name_fi: r.name_fi,
            name_sv: r.name_sv,
            whole_region: r.whole_region,
            is_homeregion: r.is_homeregion,
          });
          if (r.is_homeregion) {
            hometown = Object.values(r.municipalities).find(m => m.is_hometown);
          }
        } else {
          Object.values(r.municipalities).forEach(m => {
            m.type = 'MUNICIPALITY';
            m.municipalityId = m.id;
            m.regionId = r.id;
            if (m.is_hometown) {
              hometown = m;
            }
            localities.push(m);
          });
        }
        return localities;
      }, [])
      .sort((a, b) => {
        if (b.is_hometown || b.is_homeregion) return 1;
        if (a.is_hometown || a.is_homeregion) return -1;
        const localityNameA = a[this.nameKey];
        const localityNameB = b[this.nameKey];
        if (localityNameA > localityNameB) return 1;
        if (localityNameA < localityNameB) return -1;
        return 0;
      });

    return (
      <div>
        <FormattedMessage
          {...messages.companyHometown}
          info="Company Hometown information"
          values={{
            hometown: hometown[this.nameKey]
              ? hometown[this.nameKey]
              : intl.formatMessage(messages.hometownMissing)
          }}
        />

        <div>
          <FormattedMessage
            {...messages.companyLocalitiesInfo}
            info="Company Hometown Info"
          />
        </div>

        <label htmlFor="localityWholeCountry">
          <input
            id="localityWholeCountry"
            type="checkbox"
            checked={wholeCountry}
            onChange={this.updateLocalityWholeCountry}
          />
          <FormattedMessage
            {...messages.companyLocalitiesWholeCountryLabel}
            info="Company operates throughout the country"
          />
        </label>

        <div
          data-tip={
            intl.formatMessage(messages.localitySearchDisabledWholeCountry)
          }
          data-tip-disable={!wholeCountry}
        >
          <CurrentLocalities disabled={wholeCountry}>
            {sortedCompanyLocalities.map(l => {
              const highlight = (
                highlightThis.regionId === l.regionId
                && (
                  highlightThis.municipalityId === l.municipalityId
                  || l.whole_region
                )
              );
              return (
                <LocalityButton
                  color={(l.is_hometown || l.is_homeregion) ? 'green' : 'blue'}
                  data-tip={
                    l.is_hometown
                      ? intl.formatMessage(messages.companyHometown)
                      : intl.formatMessage(messages.companyLocalitiesRemove)
                  }
                  data-place="top"
                  data-effect="solid"
                  data-for={`tooltip-${l.regionId}-${l.municipalityId}`}
                  highlight={highlight}
                  isHome={l.is_hometown || l.is_homeregion}
                  key={`locality-selection-${l.regionId}-${l.municipalityId}`}
                  onClick={() => this.removeLocation(l)}
                >
                  {l[this.nameKey]}
                  {(l.is_hometown)
                    ? <HomeIcon />
                    : <RemoveIcon />
                  }

                  <ReactTooltip
                    id={`tooltip-${l.regionId}-${l.municipalityId}`}
                  />
                </LocalityButton>
              );
            })}
          </CurrentLocalities>

          <LocalitySearchBox
            municipalities={municipalities}
            regions={regions}
            locale={locale}
            disabled={wholeCountry}
            onSelect={this.selectLocality}
          />
        </div>
        <ReactTooltip />
      </div>
    );
  }
}

PortfolioLocalitiesEditable.propTypes = propTypes;

const CurrentLocalities = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 6px -6px;
  opacity: ${props => props.disabled ? '0.5' : '1.0'};
  pointer-events: ${props => props.disabled ? 'none' : 'auto'};
`;

const highlight = props => keyframes`
  0% {
    background-color: ${
      props.isHome
        ? props.theme['green-normal']
        : props.theme['blue-normal']
      };
  }

  50% {
    background-color: white;
  }

  100% {
    background-color: ${
      props.isHome
        ? props.theme['green-normal']
        : props.theme['blue-normal']
      };
  }
`;

const LocalityButton = styled(Button)`
  animation: ${
    props => props.highlight
      ? css`${highlight(props)} 0.25s ease-out 3`
      : ''
    };
  margin: 6px;
`;

const RemoveIcon = styled(FaTrash)`
  margin-left: 6px;
`;

const HomeIcon = styled(FaHome)`
  margin-left: 6px;
`;

export default injectIntl(PortfolioLocalitiesEditable);
